import React, { useCallback, useEffect, useMemo, useState } from 'react';
import './TemperatureDisplayDialog.scss';
import { Modal } from 'react-bootstrap';
import Loading from '../LoadingPage/Loading';
import useSensorManagement from '../../Hooks/useSensor';
import moment from 'moment';

interface TemperatureDisplayDialogProps {
    sensorInfo: Record<string, any> | null;
    isOpen: boolean;
    handleClose: () => void;
}

const TemperatureDisplayDialog: React.FC<TemperatureDisplayDialogProps> = ({ sensorInfo, handleClose, isOpen }) => {
    const [temperatureData, setTemperatureData] = useState<any>(null);
    const [temperatureErr, setTemperatureErr] = useState<string | null>(null);
    const [tempSensorLog, setTempSensorLog] = useState<any>(null);
    const [sensorLogDate, setSensorLogDate] = useState<string>(moment().format('MM-DD-YYYY'));
    const { getRetrieveFromZebraAPI, getRetrieveFromZebraLoading, getSensorsLogAPI, getSensorsLogLoading } = useSensorManagement();

    useEffect(() => {
        if (sensorInfo?.id && isOpen) {
            setSensorLogDate(moment().format('MM-DD-YYYY'));
            fetchSensorTemperatureData();
        };
    }, [sensorInfo, isOpen]);

    useEffect(() => {
        if (sensorInfo?.id && isOpen) {
            fetchSensorLog();
        }
    }, [sensorInfo, isOpen, sensorLogDate]);

    const fetchSensorTemperatureData = useCallback(async () => {
        setTemperatureErr(null);
        try {
            const res = await getRetrieveFromZebraAPI({ id: sensorInfo?.id }, true);
            if (res?.result?.sensors?.length) {
                setTemperatureData(res.result.sensors?.[0] || null);
                return;
            }
            setTemperatureErr(res?.message || 'No temperature data available.');
        } catch (error: any) {
            setTemperatureErr(error?.message || 'Failed to fetch temperature data.');
        }
    }, [sensorInfo]);

    const fetchSensorLog = useCallback(async () => {
        try {
            const res = await getSensorsLogAPI({
                RefrigeratorName: sensorInfo?.sensorRefrigerator?.refrigeratorName,
                clientCode: sensorInfo?.clientCode,
                startDate: sensorLogDate,
                endDate: sensorLogDate
            });
            setTempSensorLog(res.length ? res?.[0] : null);
        } catch (error) {
            console.error("Error while getting sensor logs:", error);
        }
    }, [sensorInfo, sensorLogDate]);

    const isToday = moment(sensorLogDate, 'MM-DD-YYYY').isSame(moment(), 'day');

    const goToPrevDay = () => {
        setSensorLogDate(moment(sensorLogDate, 'MM-DD-YYYY').subtract(1, 'day').format('MM-DD-YYYY'));
    };
    const goToNextDay = () => {
        if (!isToday) setSensorLogDate(moment(sensorLogDate, 'MM-DD-YYYY').add(1, 'day').format('MM-DD-YYYY'));
    };

    const parseTemperature = (tempString: string) => {
        if (!tempString) return { celsius: '--', fahrenheit: '--' };
        // Parse "9.53 °C | 49.15 °F" format
        const parts = tempString.split('|').map(part => part.trim());
        const celsius = parts[0]?.replace('°C', '').trim() || '--';
        const fahrenheit = parts[1]?.replace('°F', '').trim() || '--';
        return { celsius, fahrenheit };
    }

    const getStatusDotClass = (color = '') => {
        if (/green/i.test(color)) return 'green';
        if (/red/i.test(color)) return 'red';
        if (/yellow|orange/i.test(color)) return 'orange';
        return 'green';
    }

    const temp = useMemo(() => parseTemperature(temperatureData?.lastUpdatedTemperature), [temperatureData]);
    const sensorStatusDot = useMemo(() => getStatusDotClass(temperatureData?.sensorStatusColor), [temperatureData]);
    const taskStatusDot = useMemo(() => getStatusDotClass(temperatureData?.sensorTaskStatusColor), [temperatureData]);
    const tempNumberColor = sensorStatusDot.includes('red') || taskStatusDot.includes('red') ? 'text-red' : '';

    return (
        <Modal show={!!sensorInfo?.id && isOpen} centered onHide={handleClose} dialogClassName="temperature-display-modal">
            {(getRetrieveFromZebraLoading || getSensorsLogLoading) && <Loading />}
            <div className="lcd-device">
                <Modal.Header>
                    <button className='close-btn' onClick={handleClose}>✕</button>
                </Modal.Header>
                <Modal.Body>
                    <div className="lcd-screen">
                        {temperatureErr && (
                            <div className="lcd-section">
                                <div className="lcd-error-msg">{temperatureErr}</div>
                            </div>
                        )}

                        <div className="lcd-section">
                            <div className="lcd-label">TEMPERATURE</div>
                            <div className="lcd-temp-dual">
                                <div className={`lcd-temp-item celsius ${tempNumberColor}`}>
                                    <span className="lcd-temp-value">{temp.celsius}</span>
                                    <span className="lcd-temp-unit">°C</span>
                                </div>
                                <div className={`lcd-temp-item fahrenheit ${tempNumberColor}`}>
                                    <span className="lcd-temp-value">{temp.fahrenheit}</span>
                                    <span className="lcd-temp-unit">°F</span>
                                </div>
                            </div>
                        </div>

                        <div className="lcd-section">
                            <div className="lcd-label-row">
                                <div className="lcd-label">LAST UPDATED</div>
                                <button
                                    className={`lcd-refresh-btn ${getRetrieveFromZebraLoading ? 'spinning' : ''}`}
                                    onClick={() => fetchSensorTemperatureData()}
                                    disabled={getRetrieveFromZebraLoading}
                                    title="Refresh data"
                                >
                                    &#8635;
                                </button>
                            </div>
                            <div className="lcd-timestamp">{temperatureData?.lastUpdatedTime || '--'}</div>
                        </div>

                        <div className="lcd-section">
                            <div className="lcd-label">Recorded Temperature</div>
                            <div className="lcd-status-grid">
                                <div className="lcd-temp-item wrap">
                                    <span className='lcd-temp-label'>Min. Temp.</span>
                                    <span className="lcd-temp-value secondary">{tempSensorLog?.min || '--'}</span>
                                    <span className="lcd-temp-unit secondary">°C</span>
                                </div>
                                <div className="lcd-temp-item wrap">
                                    <span className='lcd-temp-label'>Max. Temp.</span>
                                    <span className="lcd-temp-value secondary">{tempSensorLog?.max || '--'}</span>
                                    <span className="lcd-temp-unit secondary">°C</span>
                                </div>
                            </div>
                            <span className="lcd-date-label">{sensorLogDate}</span>
                            <div className="lcd-date-nav">
                                <button className="lcd-nav-btn" onClick={goToPrevDay} disabled={getSensorsLogLoading}>&#8249;</button>
                                <button className="lcd-nav-btn" onClick={goToNextDay} disabled={isToday || getSensorsLogLoading}>&#8250;</button>
                            </div>
                        </div>

                        <div className="lcd-section">
                            <div className="lcd-label">STATUS</div>
                            <div className="lcd-status-grid">
                                <div className="lcd-status-item">
                                    <span className={`lcd-status-dot ${sensorStatusDot}`}></span>
                                    <span className="lcd-status-label">Sensor</span>
                                    <span className="lcd-status-value">{temperatureData?.sensorStatus || '--'}</span>
                                </div>
                                <div className="lcd-status-item">
                                    <span className={`lcd-status-dot ${taskStatusDot}`}></span>
                                    <span className="lcd-status-label">Sensor Task</span>
                                    <span className="lcd-status-value">{temperatureData?.sensorTaskStatus || '--'}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </Modal.Body>
            </div>
        </Modal >
    )
}

export default TemperatureDisplayDialog;
